import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Shell } from "@/components/Shell";
import { BrandTheme } from "@/components/BrandTheme";
import { Toaster } from "@/components/ui/sonner";
import { HideValuesProvider } from "@/components/HideValues";
import { useTheme } from "@/lib/theme";

function ThemedToaster() {
  const t = useTheme();
  const style = t === "light" ? { background: "#ffffff", border: "1px solid #e5e7eb", color: "#0f172a", boxShadow: "0 8px 24px rgba(15,23,42,0.12)" }
    : { background: "#0a0a0b", border: "1px solid #27272a", color: "#f4f4f5" };
  return <Toaster position="top-right" theme={t} toastOptions={{ style }} />;
}
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import { PrivacyPage, TermsPage } from "@/pages/Legal";
import OwnerDashboard from "@/pages/OwnerDashboard";
import OwnerHub from "@/pages/OwnerHub";
import OwnerKpi from "@/pages/OwnerKpi";
import CustomerSheet from "@/pages/CustomerSheet";
import CustomerSummary from "@/pages/CustomerSummary";
import OwnerReminders from "@/pages/OwnerReminders";
import OwnerFunnel from "@/pages/OwnerFunnel";
import OwnerPostSale from "@/pages/OwnerPostSale";
import OwnerCustomers from "@/pages/OwnerCustomers";
import OwnerSettings from "@/pages/OwnerSettings";
import OwnerLoyalty from "@/pages/OwnerLoyalty";
import OwnerHistory from "@/pages/OwnerHistory";
import OrderDetail from "@/pages/OrderDetail";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CustomerLoyalty from "@/pages/CustomerLoyalty";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import AdminBackups from "@/pages/AdminBackups";
import AdminShopRestore from "@/pages/AdminShopRestore";
import AdminDemoRequests from "@/pages/AdminDemoRequests";
import VehicleHistory from "@/pages/VehicleHistory";
import ResetPassword from "@/pages/ResetPassword";
import OwnerStock from "@/pages/OwnerStock";
import OwnerSuppliers from "@/pages/OwnerSuppliers";
import OwnerPurchases from "@/pages/OwnerPurchases";
import OwnerFinance from "@/pages/OwnerFinance";
import OwnerReconcile from "@/pages/OwnerReconcile";
import OwnerProfit from "@/pages/OwnerProfit";
import OwnerInvoices from "@/pages/OwnerInvoices";
import OwnerDRE from "@/pages/OwnerDRE";
import OwnerSuggestions from "@/pages/OwnerSuggestions";
import OwnerTireReport from "@/pages/OwnerTireReport";
import OwnerLowMargin from "@/pages/OwnerLowMargin";
import OwnerPanel from "@/pages/OwnerPanel";
import OwnerCounterSale from "@/pages/OwnerCounterSale";
import OwnerSupport from "@/pages/OwnerSupport";
import OwnerAddons from "@/pages/OwnerAddons";
import OwnerPlan from "@/pages/OwnerPlan";
import OwnerFeatures from "@/pages/OwnerFeatures";
import OwnerImport from "@/pages/OwnerImport";
import SaleDetail from "@/pages/SaleDetail";
import ReceiptPrint from "@/pages/ReceiptPrint";
import OwnerYard from "@/pages/OwnerYard";
import OwnerLabor from "@/pages/OwnerLabor";
import OwnerSatisfaction from "@/pages/OwnerSatisfaction";
import OwnerOutsourced from "@/pages/OwnerOutsourced";
import NewOrderFlow from "@/pages/NewOrderFlow";
import PublicShare from "@/pages/PublicShare";
import OwnerEmployees from "@/pages/OwnerEmployees";
import OwnerCalculators from "@/pages/OwnerCalculators";
import OwnerTeam from "@/pages/OwnerTeam";
import OwnerReportSummary from "@/pages/OwnerReportSummary";
import OwnerCommissions from "@/pages/OwnerCommissions";
import OwnerVehiclesByEmployee from "@/pages/OwnerVehiclesByEmployee";
import OwnerLosses from "@/pages/OwnerLosses";
import PublicSurvey from "@/pages/PublicSurvey";
import OwnerBookings from "@/pages/OwnerBookings";
import PublicTrack from "@/pages/PublicTrack";
import PublicBooking from "@/pages/PublicBooking";
import QrLabel from "@/pages/QrLabel";
import MechanicApp from "@/pages/MechanicApp";
import MechanicQrPrint from "@/pages/MechanicQrPrint";

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin" replace />;
  if (user.role === "owner") return <Navigate to="/owner" replace />;
  return <Navigate to="/cliente" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <HideValuesProvider>
        <BrandTheme />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/cadastro" element={<Signup />} />
            <Route path="/privacidade" element={<PrivacyPage />} />
            <Route path="/termos" element={<TermsPage />} />
            <Route path="/o/:slug" element={<Login />} />
            <Route path="/p/:code" element={<PublicShare />} />
            <Route path="/o/:slug/login" element={<Login />} />
            <Route path="/o/:slug/os/:token" element={<PublicTrack />} />
            <Route path="/os/:token" element={<PublicTrack />} />
            <Route path="/o/:slug/agendar" element={<PublicBooking />} />
            <Route path="/o/:slug/avaliar/:token" element={<PublicSurvey />} />
            <Route path="/m/:slug" element={<MechanicApp />} />
            <Route path="/m/:slug/os/:orderId" element={<MechanicApp />} />
            <Route path="/mecanico/:slug" element={<MechanicApp />} />
            <Route path="/mecanico/:slug/os/:orderId" element={<MechanicApp />} />
            <Route path="/owner/mecanicos/qr" element={<ProtectedRoute role="owner"><MechanicQrPrint /></ProtectedRoute>} />
            <Route path="/owner/mao-de-obra" element={<ProtectedRoute role="owner"><Shell><OwnerLabor /></Shell></ProtectedRoute>} />
            <Route path="/owner/satisfacao" element={<ProtectedRoute role="owner"><Shell><OwnerSatisfaction /></Shell></ProtectedRoute>} />
            <Route path="/redefinir-senha" element={<ResetPassword />} />
            <Route path="/o/:slug/redefinir-senha" element={<ResetPassword />} />
            <Route path="/criar-senha" element={<ResetPassword invite />} />
            <Route path="/o/:slug/criar-senha" element={<ResetPassword invite />} />

            <Route path="/admin" element={<ProtectedRoute role="super_admin"><Shell><SuperAdminDashboard /></Shell></ProtectedRoute>} />
            <Route path="/admin/backups" element={<ProtectedRoute role="super_admin"><Shell><AdminBackups /></Shell></ProtectedRoute>} />
            <Route path="/admin/oficinas/:id/restaurar" element={<ProtectedRoute role="super_admin"><Shell><AdminShopRestore /></Shell></ProtectedRoute>} />
            <Route path="/admin/demonstracoes" element={<ProtectedRoute role="super_admin"><Shell><AdminDemoRequests /></Shell></ProtectedRoute>} />

            <Route path="/owner" element={<ProtectedRoute role="owner"><Shell><OwnerHub /></Shell></ProtectedRoute>} />
            <Route path="/owner/ordens" element={<ProtectedRoute role="owner"><Shell><OwnerDashboard /></Shell></ProtectedRoute>} />
            <Route path="/owner/dashboard" element={<ProtectedRoute role="owner"><Shell><OwnerKpi /></Shell></ProtectedRoute>} />
            <Route path="/owner/customers" element={<ProtectedRoute role="owner"><Shell><OwnerCustomers /></Shell></ProtectedRoute>} />
            <Route path="/owner/customers/:id/crm" element={<ProtectedRoute role="owner"><Shell><CustomerSummary /></Shell></ProtectedRoute>} />
            <Route path="/owner/customers/:id/ficha" element={<ProtectedRoute role="owner"><CustomerSheet /></ProtectedRoute>} />
            <Route path="/owner/lembretes" element={<ProtectedRoute role="owner"><Shell><OwnerReminders /></Shell></ProtectedRoute>} />
            <Route path="/owner/funil" element={<ProtectedRoute role="owner"><Shell><OwnerFunnel /></Shell></ProtectedRoute>} />
            <Route path="/owner/pos-venda" element={<ProtectedRoute role="owner"><Shell><OwnerPostSale /></Shell></ProtectedRoute>} />
            <Route path="/owner/estoque" element={<ProtectedRoute role="owner"><Shell><OwnerStock /></Shell></ProtectedRoute>} />
            <Route path="/owner/fornecedores" element={<ProtectedRoute role="owner"><Shell><OwnerSuppliers /></Shell></ProtectedRoute>} />
            <Route path="/owner/compras" element={<ProtectedRoute role="owner"><Shell><OwnerPurchases /></Shell></ProtectedRoute>} />
            <Route path="/owner/financeiro" element={<ProtectedRoute role="owner"><Shell><OwnerFinance /></Shell></ProtectedRoute>} />
            <Route path="/owner/conciliar" element={<ProtectedRoute role="owner"><Shell><OwnerReconcile /></Shell></ProtectedRoute>} />
            <Route path="/owner/conciliar/:sessionId" element={<ProtectedRoute role="owner"><Shell><OwnerReconcile /></Shell></ProtectedRoute>} />
            <Route path="/owner/terceirizados" element={<ProtectedRoute role="owner"><Shell><OwnerOutsourced /></Shell></ProtectedRoute>} />
            <Route path="/owner/prejuizos" element={<ProtectedRoute role="owner"><Shell><OwnerLosses /></Shell></ProtectedRoute>} />
            <Route path="/owner/nova-ordem" element={<ProtectedRoute role="owner"><Shell><NewOrderFlow /></Shell></ProtectedRoute>} />
            <Route path="/owner/funcionarios" element={<ProtectedRoute role="owner"><Shell><OwnerEmployees /></Shell></ProtectedRoute>} />
            <Route path="/owner/calculadoras" element={<ProtectedRoute role="owner"><Shell><OwnerCalculators /></Shell></ProtectedRoute>} />
            <Route path="/owner/calculadora-precos" element={<Navigate to="/owner/calculadoras" replace />} />
            <Route path="/owner/equipe" element={<ProtectedRoute role="owner"><Shell><OwnerTeam /></Shell></ProtectedRoute>} />
            <Route path="/owner/relatorios/resumo" element={<ProtectedRoute role="owner"><Shell><OwnerReportSummary /></Shell></ProtectedRoute>} />
            <Route path="/owner/relatorios/comissoes" element={<ProtectedRoute role="owner"><Shell><OwnerCommissions /></Shell></ProtectedRoute>} />
            <Route path="/owner/relatorios/veiculos-funcionario" element={<ProtectedRoute role="owner"><Shell><OwnerVehiclesByEmployee /></Shell></ProtectedRoute>} />
            <Route path="/owner/rentabilidade" element={<ProtectedRoute role="owner"><Shell><OwnerProfit /></Shell></ProtectedRoute>} />
            <Route path="/owner/agendamentos" element={<ProtectedRoute role="owner"><Shell><OwnerBookings /></Shell></ProtectedRoute>} />
            <Route path="/owner/ordens/:id/etiqueta" element={<ProtectedRoute role="owner"><QrLabel /></ProtectedRoute>} />
            <Route path="/owner/patio" element={<ProtectedRoute role="owner"><Shell><OwnerYard /></Shell></ProtectedRoute>} />
            <Route path="/owner/balcao" element={<ProtectedRoute role="owner"><Shell><OwnerCounterSale /></Shell></ProtectedRoute>} />
            <Route path="/owner/suporte" element={<ProtectedRoute role="owner"><Shell><OwnerSupport /></Shell></ProtectedRoute>} />
            <Route path="/owner/telas-e-funcoes" element={<ProtectedRoute role="owner"><Shell><OwnerFeatures /></Shell></ProtectedRoute>} />
            <Route path="/owner/importar" element={<ProtectedRoute role="owner"><Shell><OwnerImport /></Shell></ProtectedRoute>} />
            <Route path="/owner/meu-plano" element={<ProtectedRoute role="owner"><Shell><OwnerPlan /></Shell></ProtectedRoute>} />
            <Route path="/owner/pacotes" element={<ProtectedRoute role="owner"><Shell><OwnerAddons /></Shell></ProtectedRoute>} />
            <Route path="/owner/balcao/:id" element={<ProtectedRoute role="owner"><Shell><SaleDetail /></Shell></ProtectedRoute>} />
            <Route path="/owner/balcao/:id/comprovante" element={<ProtectedRoute role="owner"><ReceiptPrint kind="sale" /></ProtectedRoute>} />
            <Route path="/owner/notas/:id/imprimir" element={<ProtectedRoute role="owner"><ReceiptPrint kind="invoice" /></ProtectedRoute>} />
            <Route path="/owner/notas" element={<ProtectedRoute role="owner"><Shell><OwnerInvoices /></Shell></ProtectedRoute>} />
            <Route path="/owner/dre" element={<ProtectedRoute role="owner"><Shell><OwnerDRE /></Shell></ProtectedRoute>} />
            <Route path="/owner/sugestao-compra" element={<ProtectedRoute role="owner"><Shell><OwnerSuggestions /></Shell></ProtectedRoute>} />
            <Route path="/owner/pneus" element={<ProtectedRoute role="owner"><Shell><OwnerTireReport /></Shell></ProtectedRoute>} />
            <Route path="/owner/margem-baixa" element={<ProtectedRoute role="owner"><Shell><OwnerLowMargin /></Shell></ProtectedRoute>} />
            <Route path="/owner/painel" element={<ProtectedRoute role="owner"><Shell><OwnerPanel /></Shell></ProtectedRoute>} />
            <Route path="/owner/notas/:id/cupom" element={<ProtectedRoute role="owner"><ReceiptPrint kind="invoice" /></ProtectedRoute>} />
            <Route path="/owner/ordens/:id/cupom" element={<ProtectedRoute role="owner"><ReceiptPrint kind="order" /></ProtectedRoute>} />
            <Route path="/owner/clube" element={<ProtectedRoute role="owner"><Shell><OwnerLoyalty /></Shell></ProtectedRoute>} />
            <Route path="/owner/historico" element={<ProtectedRoute role="owner"><Shell><OwnerHistory /></Shell></ProtectedRoute>} />
            <Route path="/owner/settings" element={<ProtectedRoute role="owner"><Shell><OwnerSettings /></Shell></ProtectedRoute>} />
            <Route path="/owner/ordens/:id" element={<ProtectedRoute role="owner"><Shell><OrderDetail /></Shell></ProtectedRoute>} />
            <Route path="/owner/veiculo/:plate" element={<ProtectedRoute role="owner"><Shell><VehicleHistory /></Shell></ProtectedRoute>} />

            <Route path="/cliente" element={<ProtectedRoute role="customer"><Shell><CustomerDashboard /></Shell></ProtectedRoute>} />
            <Route path="/cliente/clube" element={<ProtectedRoute role="customer"><Shell><CustomerLoyalty /></Shell></ProtectedRoute>} />
            <Route path="/cliente/ordens/:id" element={<ProtectedRoute role="customer"><Shell><OrderDetail readOnly /></Shell></ProtectedRoute>} />
            <Route path="/cliente/veiculo/:plate" element={<ProtectedRoute role="customer"><Shell><VehicleHistory /></Shell></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <ThemedToaster />
        </HideValuesProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
